import {
  Component,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { TabBase } from '@core/base/tab.base';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import {
  DvCountriesService,
  DvDropdownGridElement,
  DvDynamicFormUtils,
  DvFormUtils,
  FormOf,
} from '@devinco/ui/legacy';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { AddressLookupResp } from '@shared/interfaces/address-lookup';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { POIInformationVariablesTabService } from './poi-information-tab-variables.service';
import {
  GenerateLinkFormData,
  POIDto,
  POIForm,
  POIType,
} from '@pages/poi-page/interfaces';
import { FullScreenMapComponent } from '@features/map/full-screen-map/full-screen-map.component';
import {
  AutoZoomFitParams,
  Coordinates,
  FullScreenMapProps,
  MapInput,
  MarkerUpdateTriggerEvent,
} from '@features/map/interfaces';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { MapAddressSearchFormComponent } from '@shared/components/map-address-search-form/map-address-search-form.component';
import { DialogService } from '@core/services/dialog.service';
import { AddressSearchService } from '@features/map/services/address-search.service';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
import { MapId } from '@features/map/constants/map-id.enum';
import { ActivatedRoute } from '@angular/router';
import { TypedPOIService } from '@pages/poi-page/services/typed-poi.service';
import { RouteNetwork } from '@shared/interfaces/route-network';
import { RouteNetworkLayerBuilderService } from '@pages/route-network-page/services/route-network-layer-builder.service';
import { TranslateService } from '@ngx-translate/core';
import { EditDialogService } from '@shared/components/edit-dialog/edit-dialog.service';

@Component({
  selector: 'app-poi-information-tab',
  templateUrl: './poi-information-tab.component.html',
  styleUrls: ['./poi-information-tab.component.scss'],
  providers: [POIInformationVariablesTabService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class POIInformationTabComponent
  extends TabBase
  implements PendingChanges
{
  private readonly api = inject(RestApiService);
  private entityHelperService = inject(SplitterWithGridHelperService);
  readonly variables = inject(POIInformationVariablesTabService);
  private dialog = inject(DialogService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private addressSearchService = inject(AddressSearchService);
  private userCountryService = inject(UserMetadataService);
  private countriesService = inject(DvCountriesService);
  private routeNetworkLayerBuilderService = inject(
    RouteNetworkLayerBuilderService
  );
  private metaDataService = inject(UserMetadataService);
  private translate = inject(TranslateService);
  private editDialogService = inject(EditDialogService);

  formGroup: FormGroup<FormOf<Partial<POIForm>>>;
  elements: DvDynamicFormElement[];

  @ViewChild('poiMapAddressForm')
  poiMapAddressForm: TemplateRef<MapAddressSearchFormComponent>;
  canEditPOI$: Observable<boolean>;

  addressesByAddressSearch = signal<AddressLookupResp[]>([]);
  addressesByGPSSearch = signal<AddressLookupResp[]>([]);
  currentLookupAddress = signal<Partial<POIDto>>({});

  details1Elements: DvDynamicFormElement[];
  details2Elements: DvDynamicFormElement[];
  purposeElements: DvDynamicFormElement[];

  private poiInformation: POIDto | undefined;
  private markerUpdateTrigger$ = new Subject<MarkerUpdateTriggerEvent>();
  private mapMarkers$ = new BehaviorSubject<MapInput[]>([]);
  private autoZoomFit$ = new BehaviorSubject<AutoZoomFitParams>({
    doAutoFit: true,
  });
  private destroyRef = inject(DestroyRef);
  private poiMarkerService = inject(TypedPOIService);

  constructor() {
    super(inject(ActivatedRoute).parent!);
    this.setupVariables();
    this.setupEvents();
    this.handleAccessRestrictions();
  }

  setupVariables(): void {
    this.details1Elements = this.variables.details1Elements(
      this.performAddressSearch
    );
    this.details2Elements = this.variables.details2Elements(
      this.performCoordinatesSearch
    );
    this.purposeElements = this.variables.getPurposeElements(
      this.generateLinkFn
    );
    this.elements = [
      ...this.details1Elements,
      ...this.details2Elements,
      ...this.variables.acceptanceRadiusElements,
      ...this.variables.activeElement,
      ...this.purposeElements,
      ...this.variables.routeNetworkElements,
    ];
    this.formGroup = DvFormUtils.createFormGroup(this.elements, true);
  }

  hasChanges = (): boolean => this.formGroup.dirty && this.formGroup.valid;
  pendingChangesSave = (): Observable<boolean> => this.performSave();

  onSave(): void {
    this.performSave().subscribe();
  }

  onSearchClosed(): void {
    this.addressesByAddressSearch.set([]);
    this.addressesByGPSSearch.set([]);
  }

  openEditMap(): void {
    this.currentLookupAddress.set(this.formGroup.getRawValue());
    const { GPSLat, GPSLon, POITypeID } = this.formGroup.controls;

    combineLatest([
      this.api.getCustom<RouteNetwork[]>('Route/MyNetworks'),
      POITypeID?.value ? this.getPoiTypeByID(POITypeID.value) : of(undefined),
    ])
      .pipe(take(1))
      .subscribe(([routeNetworks, poiTypeData]) => {
        const routeNetworkPoints =
          this.routeNetworkLayerBuilderService.mapRouteNetworkToMapMarkerInput(
            routeNetworks
          );

        // New POIs have no loaded poiInformation, so fall back to the form values
        const poiData = this.poiInformation ?? this.formGroup.getRawValue();
        const poiPoints = this.poiMarkerService.getTypedPOIMarkers(
          [
            {
              ...poiData,
              GPSLat: GPSLat?.value ?? poiData.GPSLat,
              GPSLon: GPSLon?.value ?? poiData.GPSLon,
              IconColor: poiTypeData?.IconColor,
              IconKey: poiTypeData?.IconKey,
              Severity_TranslationKey: poiTypeData?.Severity_TranslationKey,
              POIType_TranslationKey: poiTypeData?.TranslationKey,
            },
          ],
          true
        );
        const poi = poiPoints.length ? [{ points: poiPoints }] : [];

        this.mapMarkers$.next([...poi, ...routeNetworkPoints]);

        const data: FullScreenMapProps = {
          id: MapId.PoiFullScreenMap,
          markers$: this.mapMarkers$,
          resetAllMarkersOnClick: true,
          addNewMarkerOnDblClick:
            this.entityId$.getValue() === NEW_ENTITY_ROUTE_PARAM,
          newMarkerIconConfig: poiTypeData
            ? {
                ...this.poiMarkerService.getTypedPOIMarkers(
                  [
                    {
                      GPSLat: 0,
                      GPSLon: 0,
                      IconColor: poiTypeData.IconColor,
                      IconKey: poiTypeData.IconKey,
                      Severity_TranslationKey:
                        poiTypeData.Severity_TranslationKey,
                      POIType_TranslationKey: poiTypeData.TranslationKey,
                    },
                  ],
                  true
                )[0],
              }
            : undefined,
          customTemplate: {
            template: this.poiMapAddressForm,
            isFullScreenTemplate: true,
          },
          markerUpdateTrigger$: this.markerUpdateTrigger$,
          autoZoomFit$: this.autoZoomFit$,
          zoom: 5,
        };

        this.dialog.open<FullScreenMapProps, FullScreenMapComponent>({
          size: 'full',
          contentComponent: FullScreenMapComponent,
          hideHeader: true,
          hideFooter: true,
          data,
        });
      });
  }

  performAddressSearch = (): void => {
    const controls = this.formGroup.controls;

    this.addressSearchService
      .getGPSPositionFromAddress({
        Address: controls.Address?.value,
        PostalPlace: controls.PostalPlace?.value,
      })
      .subscribe(resp => {
        if (!resp.length) {
          this.addressSearchService.showNoAddressHitInfoMsg();
          return;
        }
        if (resp.length === 1) {
          this.formGroup.patchValue({
            ...resp[0],
            GPSLat: resp[0].Position?.Latitude,
            GPSLon: resp[0].Position?.Longitude,
          });
          this.formGroup.markAsDirty();
          return;
        }

        this.addressesByAddressSearch.set(resp);
      });
  };

  performCoordinatesSearch = (): void => {
    const controls = this.formGroup.controls;

    if (!controls.GPSLat?.value || !controls.GPSLon?.value) return;

    this.addressSearchService
      .getAddressFromGPSPosition({
        lat: controls.GPSLat.value,
        lng: controls.GPSLon.value,
      })
      .subscribe(resp => {
        if (!resp.length) {
          this.addressSearchService.showNoAddressHitInfoMsg();
          return;
        }

        if (resp.length === 1) {
          this.formGroup.patchValue(resp[0]);
          this.formGroup.markAsDirty();
        } else {
          this.addressesByGPSSearch.set(resp);
        }
      });
  };

  onMapConfirmBtnClicked(formData?: Partial<POIDto> | undefined): void {
    if (formData) {
      this.formGroup.patchValue(formData);
      this.formGroup.markAsDirty();
    } else {
      this.currentLookupAddress.set(this.formGroup.getRawValue());
    }
    this.dialog.closeAllDialogs();
  }

  onAddressByAddressSearchSelected(address: AddressLookupResp): void {
    const addressPosition = address.Position;
    this.currentLookupAddress.set({
      ...address,
      GPSLat: addressPosition?.Latitude,
      GPSLon: addressPosition?.Longitude,
    });
    if (addressPosition) {
      this.formGroup.controls.GPSLat?.setValue(addressPosition.Latitude);
      this.formGroup.controls.GPSLon?.setValue(addressPosition.Longitude);
    }
    this.formGroup.patchValue(this.currentLookupAddress());

    if (addressPosition?.Latitude && addressPosition.Longitude) {
      this.setNewMarkerPosition({
        lat: addressPosition.Latitude,
        lng: addressPosition.Longitude,
      });
    }
    this.formGroup.markAsDirty();
  }

  onAddressByGPSSearchSelected(address: AddressLookupResp): void {
    const currentFormGroupValue = this.formGroup.value;
    this.currentLookupAddress.set({
      ...address,
      GPSLat: currentFormGroupValue.GPSLat,
      GPSLon: currentFormGroupValue.GPSLon,
    });
    this.formGroup.patchValue(this.currentLookupAddress());

    this.formGroup.markAsDirty();
  }

  onMapCoordinatesChanged(coordinates: Coordinates): void {
    this.addressSearchService
      .getAddressFromGPSPosition(coordinates)
      .subscribe(resp => {
        if (!resp.length) {
          this.addressSearchService.showNoAddressHitInfoMsg();
          return;
        }

        if (resp.length === 1 && coordinates.lat && coordinates.lng) {
          this.currentLookupAddress.set(resp[0]);
          this.setNewMarkerPosition(coordinates);
        } else {
          this.addressesByGPSSearch.set(resp);
        }
      });
  }

  onAddressOrPostalPlaceChanges(
    details: Pick<POIDto, 'Address' | 'PostalPlace'>
  ): void {
    this.addressSearchService
      .getGPSPositionFromAddress(details)
      .subscribe(resp => {
        if (!resp.length) {
          this.addressSearchService.showNoAddressHitInfoMsg();
          this.currentLookupAddress.set({});
          this.mapMarkers$.next([]);
          return;
        }

        if (resp.length === 1) {
          if (!resp[0].Position) return;
          this.currentLookupAddress.set({
            ...resp[0],
            GPSLat: resp[0].Position.Latitude,
            GPSLon: resp[0].Position.Longitude,
          });
          this.setNewMarkerPosition({
            lat: Number(resp[0].Position.Latitude.toFixed(5)),
            lng: Number(resp[0].Position.Longitude.toFixed(5)),
          });
        } else {
          this.addressesByAddressSearch.set(resp);
        }
      });
  }

  generateLinkFn = (): void => {
    this.editDialogService
      .openEditDialog<GenerateLinkFormData>({
        title: 'POI.GenerateLinkDialog.Title',
        elements: this.variables.getLinkGenerationElements(),
        bannerConfig: { contentText: 'POI.GenerateLinkDialog.BannerText' },
      })
      .closed.pipe(
        take(1),
        tap(resp => {
          if (resp.source !== 'confirm') return;

          const responseData: GenerateLinkFormData = resp.responseData;
          const text = responseData.displayText || responseData.link;
          const link = `\n <a href="${responseData.link}" target="_blank" rel="noopener noreferrer">${text}</a>`;

          const { Purpose } = this.formGroup.controls;
          Purpose?.setValue((Purpose.value || '') + link);
          Purpose?.markAsDirty();
        })
      )
      .subscribe();
  };

  private performSave(): Observable<boolean> {
    const entityId =
      this.entityId$.value === NEW_ENTITY_ROUTE_PARAM
        ? '0'
        : this.entityId$.value;

    const data = {
      ...this.formGroup.getRawValue(),
      Active: !!this.formGroup.controls.Active?.value,
    };

    delete data.coordinatesSearchBtn;
    delete data.addressSearchBtn;
    delete data.generateLink;

    if (data.CountryCode) {
      data.Country = this.countriesService
        .getEnglishCountryNames()
        .get(data.CountryCode);
    }

    return this.api.upsert(Entity.POI, entityId, data).pipe(
      tap((id: number | undefined) => {
        if (id) {
          this.entityHelperService.addNewlyCreatedItemToGrid(Entity.POI, id);
        } else {
          this.entityHelperService.refreshRowData(entityId);
        }
        this.formGroup.markAsPristine();
      }),
      map(() => true)
    );
  }

  private setupEvents(): void {
    this.listenToEntityChange();
    this.listenToNewMarkerTrigger();
    this.listenToCountryChanges();
  }

  private listenToEntityChange(): void {
    this.entityId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(id => {
      this.setPoiMarkerInfo(undefined);
      if (!id) return;
      if (id === NEW_ENTITY_ROUTE_PARAM) {
        this.currentLookupAddress.set({});
        this.formGroup.reset();
        this.formGroup.controls.Country?.setValue(
          this.userCountryService.getUserCountry()
        );
        if (this.metaDataService.hasSkiDomain()) {
          this.formGroup.controls.IsPublic?.setValue(true, {
            emitEvent: true,
          });
        }
        this.formGroup.controls.AcceptanceRadius?.setValue(50, {
          emitEvent: false,
        });
        this.formGroup.controls.Active?.setValue(true, { emitEvent: false });
        return;
      }

      const keys = DvDynamicFormUtils.getKeysForRequest(this.elements);
      this.api
        .getInfo<POIForm>(Entity.POI, id, keys)
        .pipe(
          switchMap(res => {
            if (res.POITypeID) {
              return this.getPoiTypeByID(res.POITypeID).pipe(
                map(
                  poiTypeData =>
                    ({ ...res, Name: poiTypeData.Name }) satisfies POIForm
                )
              );
            } else {
              return of(res);
            }
          })
        )
        .subscribe(res => {
          this.setPoiMarkerInfo(res);
          DvDynamicFormUtils.setFirstData(this.elements, this.formGroup, res);
          this.setPoiTypeDropdownValues(res);

          this.currentLookupAddress.set(res);
        });
    });
  }

  private setPoiTypeDropdownValues(res: POIForm): void {
    if (!res.POITypeID) return;
    (
      this.elements.find(
        el => el.options.key === 'POITypeID'
      ) as DvDropdownGridElement
    ).options.selectedValue = {
      TranslationKey: this.translate.instant(res.POIType_TranslationKey || ''),
      POITypeID: res.POITypeID,
    };
    this.formGroup.controls.POITypeID?.setValue(res.POITypeID);
  }

  private setPoiMarkerInfo(poi: POIDto | undefined): void {
    this.poiInformation = poi;
  }

  private getPoiTypeByID(id: number): Observable<POIType> {
    return this.api.getInfo<Partial<POIType>>(Entity.POIType, String(id), [
      'Name',
      'Description',
      'POITypeID',
      'IconKey',
      'IconColor',
      'Severity_TranslationKey',
      'TranslationKey',
      'Description_TranslationKey',
    ]);
  }

  private listenToNewMarkerTrigger(): void {
    this.markerUpdateTrigger$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newMarker => {
        this.addressSearchService
          .getAddressFromGPSPosition(newMarker)
          .subscribe(resp => {
            this.currentLookupAddress.set({
              ...resp[0],
              GPSLat: newMarker.lat,
              GPSLon: newMarker.lng,
            });
          });
      });
  }

  private listenToCountryChanges(): void {
    const { controls } = this.formGroup;
    controls.CountryCode?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        controls.GPSLat?.reset();
        controls.GPSLon?.reset();
      });
  }

  private handleAccessRestrictions(): void {
    this.canEditPOI$ = this.permissionsService.hasPermissions([
      RolesAndPermissions.POIEdit,
    ]);
    this.canEditPOI$.pipe(take(1)).subscribe(hasPermissions => {
      if (!hasPermissions) {
        this.formGroup.disable();
        this.details1Elements = this.details1Elements.map(el => ({
          ...el,
          options: {
            ...el.options,
            disabled: el.options.key === 'addressSearchBtn',
          },
        }));
        this.details2Elements = this.details2Elements.map(el => ({
          ...el,
          options: {
            ...el.options,
            disabled: el.options.key === 'coordinatesSearchBtn',
          },
        }));
      }
    });
  }

  private setNewMarkerPosition(coordinates: Coordinates | undefined): void {
    if (coordinates) {
      this.mapMarkers$.next([{ points: [coordinates] }]);
      setTimeout(() => {
        this.autoZoomFit$.next({ doAutoFit: true });
      });
    }
  }
}
